import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMembershipSubscription } from '../components/trashie/utils/service';
import { formatTransactionDate } from '../components/trashie/utils/dates';
import { formatMoney } from '../utils/formatMoney';
import { SUBSCRIPTION_STATUSES } from '../components/trashie/views/ManageMembership/utils';

const COLORS = {
  ACTIVE: 'var(--japan-color-hard-mint)',
  INACTIVE: '#ff0000',
};

const useSubscriptionStatus = () => {
  const [membershipSubscriptionData, setMembershipSubscriptionData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const getMembershipSubscriptionData = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);

    const { data, error } = await getMembershipSubscription();

    if (error || !data?.uid) {
      setHasError(true);
      setMembershipSubscriptionData({});
    } else {
      setMembershipSubscriptionData(data);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    getMembershipSubscriptionData();
  }, []);

  const subscriptionStatus = useMemo(() => {
    const {
      stripeStatus,
      cancelAtPeriodEnd,
      currentPeriodEnd,
      amount,
    } = membershipSubscriptionData;

    if (!currentPeriodEnd) {
      return {
        label: 'Loading...',
        color: COLORS.ACTIVE,
        message: 'Loading membership information...',
      };
    }

    const now = moment();
    const periodEnd = moment(currentPeriodEnd);
    const isExpired = periodEnd.isBefore(now);

    const defaultMessage = `Membership ended on ${formatTransactionDate(currentPeriodEnd)}`;

    if (stripeStatus === SUBSCRIPTION_STATUSES.ACTIVE) {
      if (cancelAtPeriodEnd) {
        return {
          label: 'Active (Cancelling)',
          color: COLORS.ACTIVE,
          message: `Access until ${formatTransactionDate(currentPeriodEnd)} · Will not renew`,
        };
      }
      return {
        label: 'Active',
        color: COLORS.ACTIVE,
        message: `Your Trashie Unlimited membership will renew on ${formatTransactionDate(currentPeriodEnd)} for ${formatMoney(amount)}`,
      };
    }

    if (stripeStatus === SUBSCRIPTION_STATUSES.CANCELLED) {
      return {
        label: 'Inactive',
        color: COLORS.INACTIVE,
        message: defaultMessage,
      };
    }

    if (isExpired) {
      return {
        label: 'Expired',
        color: COLORS.INACTIVE,
        message: defaultMessage,
      };
    }

    return {
      label: 'Inactive',
      color: COLORS.INACTIVE,
      message: defaultMessage,
    };
  }, [membershipSubscriptionData]);

  return {
    subscriptionStatus,
    membershipSubscriptionData,
    isLoading,
    hasError,
    getMembershipSubscriptionData,
    setMembershipSubscriptionData,
  };
};

export default useSubscriptionStatus;
